import { DOCUMENT, inject } from '@angular/core';
import { Routes } from '@angular/router';
import { canActivateAuthGuard } from '@okta/okta-angular';
import { OAuthCallbackPage } from './core/oauth-callback-page';
import { SignedOutPage } from './core/signed-out-page';
import { ASYNC_TIMEOUT_RETRY } from './core/async-error';

import { RoleLandingPage } from './role-landing-page';
import { UnauthorizedPage } from './unauthorized-page';
import {
  adminRoleGuard,
  approverRoleGuard,
  roleLandingGuard,
  userRoleGuard,
} from './core/role.guards';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: RoleLandingPage,
    canActivate: [canActivateAuthGuard, roleLandingGuard],
  },

  { path: 'login/callback', component: OAuthCallbackPage },
  { path: 'signed-out', component: SignedOutPage },
  { path: 'unauthorized', component: UnauthorizedPage, canActivate: [canActivateAuthGuard] },

  {
    path: 'user',
    pathMatch: 'prefix',
    canActivate: [canActivateAuthGuard, userRoleGuard],
    providers: [{
      provide: ASYNC_TIMEOUT_RETRY,
      useFactory: () => {
        const document = inject(DOCUMENT);
        return () => document.location.reload();
      },
    }],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      { path: 'dashboard', loadComponent: () => import('./features/user/user-dashboard-view').then(m => m.UserDashboardView), data: { page: 'user-dashboard' } },
      { path: 'certificates', loadComponent: () => import('./features/user/user-certificates-view').then(m => m.UserCertificatesView), data: { page: 'user-certificates' } },
      { path: 'certificates/upload', loadComponent: () => import('./features/user/user-upload-view').then(m => m.UserUploadView), data: { page: 'user-upload' } },
      { path: 'certificates/:id', loadComponent: () => import('./features/user/user-certificate-detail-view').then(m => m.UserCertificateDetailView), data: { page: 'user-certificate-detail' } },
      { path: 'hub-ca-chain', loadComponent: () => import('./features/user/user-hub-ca-chain-view').then(m => m.UserHubCaChainView), data: { page: 'user-hub-ca-chain' } },
      { path: 'requests', loadComponent: () => import('./features/user/user-requests-view').then(m => m.UserRequestsView), data: { page: 'user-requests' } },
      { path: 'requests/:id', loadComponent: () => import('./features/user/user-request-detail-view').then(m => m.UserRequestDetailView), data: { page: 'user-request-detail' } },
      { path: 'organization', loadComponent: () => import('./features/user/user-organization-view').then(m => m.UserOrganizationView), data: { page: 'user-organization' } },
    ],
  },

  {
    path: 'approver',
    pathMatch: 'prefix',
    canActivate: [canActivateAuthGuard, approverRoleGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      { path: 'dashboard', loadComponent: () => import('./features/approver/approver-dashboard-view').then(m => m.ApproverDashboardView), data: { page: 'approver-dashboard' } },
      { path: 'approvals', loadComponent: () => import('./features/approver/approver-approvals-view').then(m => m.ApproverApprovalsView), data: { page: 'approver-approvals' } },
      { path: 'approvals/:id', loadComponent: () => import('./features/approver/approver-approval-detail-view').then(m => m.ApproverApprovalDetailView), data: { page: 'approver-approval-detail' } },
      { path: 'certificates', loadComponent: () => import('./features/approver/approver-certificates-view').then(m => m.ApproverCertificatesView), data: { page: 'approver-certificates' } },
      { path: 'scheduled-changes', loadComponent: () => import('./features/approver/approver-scheduled-changes-view').then(m => m.ApproverScheduledChangesView), data: { page: 'approver-scheduled-changes' } },
      { path: 'audit-history', loadComponent: () => import('./features/approver/approver-audit-history-view').then(m => m.ApproverAuditHistoryView), data: { page: 'approver-audit-history' } },
      { path: 'alerts', loadComponent: () => import('./features/approver/approver-alerts-view').then(m => m.ApproverAlertsView), data: { page: 'approver-alerts' } },
    ],
  },

  {
    path: 'admin',
    pathMatch: 'prefix',
    canActivate: [canActivateAuthGuard, adminRoleGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      { path: 'dashboard', loadComponent: () => import('./features/admin/admin-dashboard-view').then(m => m.AdminDashboardView), data: { page: 'admin-dashboard' } },
      { path: 'requests', loadComponent: () => import('./features/admin/admin-requests-view').then(m => m.AdminRequestsView), data: { page: 'admin-requests' } },
      { path: 'requests/:id', loadComponent: () => import('./features/admin/admin-request-detail-view').then(m => m.AdminRequestDetailView), data: { page: 'admin-request-detail' } },
      { path: 'organizations', loadComponent: () => import('./features/admin/admin-organizations-view').then(m => m.AdminOrganizationsView), data: { page: 'admin-organizations' } },
      { path: 'organizations/:id', loadComponent: () => import('./features/admin/admin-organization-detail-view').then(m => m.AdminOrganizationDetailView), data: { page: 'admin-organization-detail' } },
      { path: 'cert-repository', loadComponent: () => import('./features/admin/admin-cert-repository-view').then(m => m.AdminCertRepositoryView), data: { page: 'admin-cert-repository' } },
      { path: 'cert-repository/:id', loadComponent: () => import('./features/admin/admin-certificate-detail-view').then(m => m.AdminCertificateDetailView), data: { page: 'admin-certificate-detail' } },
      { path: 'gateway-ops', loadComponent: () => import('./features/admin/admin-gateway-ops-view').then(m => m.AdminGatewayOpsView), data: { page: 'admin-gateway-ops' } },
      { path: 'gateway-ops/:id', loadComponent: () => import('./features/admin/admin-gateway-operation-detail-view').then(m => m.AdminGatewayOperationDetailView), data: { page: 'admin-gateway-operation-detail' } },
      { path: 'hub-outbound', loadComponent: () => import('./features/admin/admin-hub-outbound-view').then(m => m.AdminHubOutboundView), data: { page: 'admin-hub-outbound' } },
      { path: 'api-clients', loadComponent: () => import('./features/admin/admin-api-clients-view').then(m => m.AdminApiClientsView), data: { page: 'admin-api-clients' } },
      { path: 'audit-log', loadComponent: () => import('./features/admin/admin-audit-log-view').then(m => m.AdminAuditLogView), data: { page: 'admin-audit-log' } },
    ],
  },

  // Legacy user URLs remain available only to users who actually have the user role.
  { path: 'dashboard', component: RoleLandingPage, canActivate: [canActivateAuthGuard, roleLandingGuard] },
  { path: 'certificates', redirectTo: 'user/certificates', pathMatch: 'full' },
  { path: 'certificates/upload', redirectTo: 'user/certificates/upload', pathMatch: 'full' },
  { path: 'certificates/:id', redirectTo: 'user/certificates/:id' },
  { path: 'hub-ca-chain', redirectTo: 'user/hub-ca-chain', pathMatch: 'full' },
  { path: 'requests', redirectTo: 'user/requests', pathMatch: 'full' },
  { path: 'requests/:id', redirectTo: 'user/requests/:id' },
  { path: 'organization', redirectTo: 'user/organization', pathMatch: 'full' },

  {
    path: '**',
    component: RoleLandingPage,
    canActivate: [canActivateAuthGuard, roleLandingGuard],
  },
];
